<template>
  <div>
    <el-form :model="query" :inline="true" size="small">
      <el-form-item label="客户：">
        <el-input v-model="query.customerKeywords" placeholder="姓名/手机号" clearable
                  style="width:170px"
                  @keyup.enter.native="handleQuery" />
      </el-form-item>
      <el-form-item v-if="!lecturerMode" label="课程：">
        <el-select v-model="query.subjectId" placeholder="输入课程名称搜索" clearable filterable remote
                   :remote-method="searchCourses" :loading="courseLoading" style="width:230px">
          <el-option v-for="item in courseOptions" :key="item.key" :label="item.value" :value="item.key" />
        </el-select>
      </el-form-item>
      <el-form-item label="是否通过：">
        <el-select v-model="query.passed" placeholder="全部" clearable style="width:100px">
          <el-option label="通过" :value="true" />
          <el-option label="未通过" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态：">
        <el-select v-model="query.status" placeholder="全部" clearable style="width:110px">
          <el-option label="进行中" :value="1" />
          <el-option label="已交卷" :value="2" />
          <el-option label="已作废" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="交卷时间：">
        <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd"
                        range-separator="至"
                        start-placeholder="开始" end-placeholder="结束" style="width:240px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" size="mini" @click="handleQuery">搜索</el-button>
        <el-button type="info" size="mini" plain @click="handleReset">重置</el-button>
        <el-button v-if="!lecturerMode" type="primary" size="mini" plain @click="handleExport">导出</el-button>
      </el-form-item>
    </el-form>

    <el-alert v-if="listError" :title="listError" type="error" :closable="false" show-icon />
    <el-table v-loading="listLoading" :data="list" :empty-text="listError ? '查询未成功，请重试' : '暂无数据'" border size="small">
      <el-table-column label="客户" align="center" min-width="110">
        <template slot-scope="{row}">
          {{ row.customerName }}<br><span class="sub">{{ row.customerPho }}</span>
        </template>
      </el-table-column>
      <el-table-column label="课程" prop="subjectName" align="center" min-width="140" />
      <el-table-column label="视频课时" prop="videoName" align="center" min-width="130" />
      <el-table-column label="第几次" prop="attemptNo" align="center" width="70" />
      <el-table-column label="本次得分" prop="score" align="center" width="90">
        <template slot-scope="{row}">{{ scoreText(row.score) }}</template>
      </el-table-column>
      <el-table-column label="最终成绩" align="center" width="100">
        <template slot="header">
          最终成绩
          <el-tooltip content="按该考试配置的规则（最高分/最近一次/首次）在所有已交卷的次数里取一个；作废的不参与" placement="top"><i class="el-icon-question" /></el-tooltip>
        </template>
        <template slot-scope="{row}">{{ scoreText(row.finalScore) }}</template>
      </el-table-column>
      <el-table-column label="是否通过" align="center" width="90">
        <template slot-scope="{row}">
          <el-tag :type="paperResult(row).type" size="mini">{{ paperResult(row).text }}</el-tag>
        </template>
      </el-table-column>
      <!-- 发的是学分，不是积分（ISSUE-2026-392021）；接口字段 grantedPoints 不改 -->
      <el-table-column label="获得学分" prop="grantedPoints" align="center" width="90">
        <template slot="header">
          获得学分
          <el-tooltip content="该考试累计已发学分（成绩提升跨档只补差额，这里是补后的总数）" placement="top"><i class="el-icon-question" /></el-tooltip>
        </template>
        <template slot-scope="{row}">{{ row.grantedPoints === null || row.grantedPoints === undefined ? '—' : row.grantedPoints }}</template>
      </el-table-column>
      <el-table-column label="用时" align="center" width="90">
        <template slot-scope="{row}">{{ row.usedSecond | usedTime }}</template>
      </el-table-column>
      <el-table-column label="状态" align="center" width="110">
        <template slot-scope="{row}">
          <span :class="{ 'void-row': row.status === 3 }">{{ row.status | statusText }}</span>
          <el-tooltip v-if="row.autoSubmitted" content="到时自动交卷" placement="top"><i class="el-icon-time" /></el-tooltip>
          <el-tooltip v-if="row.status === 3 && row.voidReason" :content="'作废原因：' + row.voidReason" placement="top"><i class="el-icon-warning-outline" /></el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="交卷时间" prop="submittedAt" align="center" width="150" />
      <el-table-column label="操作" align="center" width="150" fixed="right">
        <template slot-scope="{row}">
          <el-button type="text" size="mini" @click="openDetail(row)">详情</el-button>
          <!-- 只有已交卷的能作废：进行中的用户还在答，作废了他交卷时会撞状态 -->
          <el-button v-if="!lecturerMode && row.status === 2" type="text" size="mini" @click="openVoid(row)">作废</el-button>
        </template>
      </el-table-column>
    </el-table>
    <pagination v-show="total > 0" :total="total" :page-no.sync="query.pageNo"
                :page-size.sync="query.pageSize"
                @pagination="doQueryList" />

    <!-- 答题详情：管理端一律显示正确答案与解析 -->
    <el-dialog title="答题详情" :visible.sync="detail.visible" width="820px"
               top="5vh"
               append-to-body>
      <div v-loading="detail.loading">
        <div v-if="detail.data" class="detail-head">
          第 {{ detail.data.attemptNo }} 次 · 得分 {{ scoreText(detail.data.score) }} ·
          {{ paperResult(detail.data, true).text }} ·
          正确 {{ detail.data.correctNum }} / 错误 {{ detail.data.wrongNum }} / 未答 {{ detail.data.unansweredNum }} ·
          用时 {{ detail.data.usedSecond | usedTime }}
          <span v-if="detail.data.status === 3" class="void-row">（已作废：{{ detail.data.voidReason }}）</span>
        </div>
        <div v-for="t in (detail.data && detail.data.topics) || []" :key="t.topicId" class="topic">
          <div class="topic-title">
            {{ t.topicOrder }}. {{ t.title }}
            <el-tag :type="topicResult(t, detail.data).type" size="mini">{{ topicResult(t, detail.data).text }}</el-tag>
            <span class="sub">本题得分：{{ scoreText(t.score, ' 分') }}</span>
          </div>
          <div v-for="o in t.options" :key="o.id" class="option">
            <span :class="optionClass(o, t)">{{ o.code }}. {{ o.content }}</span>
            <img v-if="o.imgUrl" :src="o.imgUrl" alt="选项图片" class="option-image" />
          </div>
          <div class="resolution">
            正确答案：{{ codesOf(t, t.correctAnswerIds) || '—' }}
            学员作答：{{ codesOf(t, t.myAnswerIds) || '未答' }}
          </div>
          <div v-if="t.resolutionContent" class="resolution">解析：{{ t.resolutionContent }}</div>
        </div>
      </div>
    </el-dialog>

    <!-- 作废：原因必填 -->
    <el-dialog title="作废答卷" :visible.sync="voidForm.visible" width="480px" append-to-body>
      <div class="void-tip">
        作废后：这一次的成绩不再参与最终成绩，<b>考试次数返还一次</b>；<b>已发放的学分不退回</b>。
      </div>
      <el-input v-model="voidForm.reason" type="textarea" :rows="3"
                maxlength="500" show-word-limit
                placeholder="请填写作废原因（必填）" />
      <span slot="footer">
        <el-button size="small" @click="voidForm.visible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="voidForm.saving" @click="submitVoid">确定作废</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import courseExamApi from '@/api/course/courseExam'
import lecturerExamApi from '@/api/course/courseExamLecturer'
import courseSubjectApi from '@/api/course/courseSubject'
import Pagination from '@/components/Pagination'

const missingScore = score => score === null || score === undefined || (typeof score === 'string' && !score.trim())

/**
 * 考试记录 ·「课后考试」页签（REQ-2026-403009）
 *
 * 数据源是 course_exam_paper，与旧「类型考试」「课程考试」完全分开：
 * 那两种没有次数/最终成绩/学分/用时的概念，混在一张表里那几列常年空白，
 * 会被当成"数据丢了"报上来。2026-09-18 与产品确认走独立页签。
 */
export default {
  name: 'ExamRecordsAfterClass',
  props: { lecturerMode: { type: Boolean, default: false }, subjectId: { type: [Number, String], default: null } },
  components: { Pagination },
  filters: {
    statusText(v) {
      return { 1: '进行中', 2: '已交卷', 3: '已作废' }[v] || '—'
    },
    usedTime(second) {
      if (!second || second <= 0) return '—'
      const m = Math.floor(second / 60)
      const s = second % 60
      return m > 0 ? `${m}分${s}秒` : `${s}秒`
    }
  },
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuerySeq: 0,
      listError: '',
      dateRange: [],
      query: { pageNo: 1, pageSize: 10, subjectId: null },
      courseOptions: [],
      courseLoading: false,
      courseSearchSeq: 0,
      detail: { visible: false, loading: false, data: null },
      voidForm: { visible: false, saving: false, reason: '', paperId: null }
    }
  },
  mounted() {
    if (!this.lecturerMode) this.searchCourses('')
    this.doQueryList({ pageNo: 1 })
  },
  beforeDestroy() {
    this.listQuerySeq++
    this.courseSearchSeq++
  },
  methods: {
    scoreText(score, suffix = '') {
      return missingScore(score) ? '—' : String(score) + suffix
    },
    paperResult(paper, fromDetail = false) {
      if (paper.status === 1) return { text: '未判定', type: 'info' }
      if (paper.status !== 2 && paper.status !== 3) return { text: '—', type: 'info' }
      if (paper.passed === true) return { text: '通过', type: 'success' }
      // 列表保留原始 Boolean|null；详情接口会将历史 NULL 压成 false。
      // 详情缺成绩时无法确认 false 是否为已判失败，保守展示未知，不按分值重算。
      if (paper.passed === false && (!fromDetail || !missingScore(paper.score))) return { text: '未通过', type: 'danger' }
      return { text: '—', type: 'info' }
    },
    topicResult(topic, paper) {
      const answer = topic.myAnswerIds
      if (answer === null || answer === undefined || (typeof answer === 'string' && !answer.split(',').some(id => id.trim()))) {
        return { text: '未答', type: 'info' }
      }
      if (typeof answer !== 'string') return { text: '—', type: 'info' }
      if (paper.status === 1) return { text: '未判定', type: 'info' }
      if (paper.status !== 2 && paper.status !== 3) return { text: '—', type: 'info' }
      if (topic.isCorrect === true) return { text: '正确', type: 'success' }
      // 同样存在 NULL 被详情接口压成 false 的情况；缺成绩时不冒称已判错。
      if (topic.isCorrect === false && !missingScore(topic.score)) return { text: '错误', type: 'danger' }
      return { text: '—', type: 'info' }
    },
    searchCourses(keyword) {
      const seq = ++this.courseSearchSeq
      this.courseLoading = true
      courseSubjectApi.fetchForOptions({ matchValue: keyword || undefined, limit: 50 }).then(({ data }) => {
        if (seq !== this.courseSearchSeq) return
        const selected = this.courseOptions.filter(item => item.key === this.query.subjectId)
        const seen = new Set()
        this.courseOptions = [...selected, ...(data || [])].filter(item => !seen.has(item.key) && seen.add(item.key))
      }).catch(() => {
        if (seq === this.courseSearchSeq) this.courseOptions = this.courseOptions.filter(item => item.key === this.query.subjectId)
      }).finally(() => {
        if (seq === this.courseSearchSeq) this.courseLoading = false
      })
    },
    handleQuery() {
      this.doQueryList({ pageNo: 1 })
    },
    handleReset() {
      this.query = { pageNo: 1, pageSize: this.query.pageSize, subjectId: null }
      this.dateRange = []
      this.doQueryList({ pageNo: 1 })
    },
    buildQuery() {
      const q = { ...this.query }
      if (this.lecturerMode) q.subjectId = this.subjectId
      // 后端按 submitted_at 过滤，结束日期要包含当天
      q.startTime = this.dateRange && this.dateRange[0] ? this.dateRange[0] + ' 00:00:00' : undefined
      q.endTime = this.dateRange && this.dateRange[1] ? this.dateRange[1] + ' 23:59:59' : undefined
      return q
    },
    doQueryList({ pageNo, pageSize }) {
      if (pageNo) this.query.pageNo = pageNo
      if (pageSize) this.query.pageSize = pageSize
      const seq = ++this.listQuerySeq
      this.listLoading = true
      this.listError = ''
      this.list = []
      this.total = 0
      const fetch = this.lecturerMode ? lecturerExamApi.papers : courseExamApi.fetchPaperList
      fetch(this.buildQuery()).then(({ data }) => {
        if (seq !== this.listQuerySeq) return
        this.list = (data && data.list) || []
        this.total = (data && data.total) || 0
      }).catch(() => {
        if (seq !== this.listQuerySeq) return
        this.list = []
        this.total = 0
        this.listError = '查询失败，请重试'
      }).finally(() => {
        if (seq === this.listQuerySeq) this.listLoading = false
      })
    },
    handleExport() {
      if (this.lecturerMode) return
      this.$common.confirm('是否确认导出')
        .then(() => courseExamApi.exportPaper(this.buildQuery()))
        .then(res => {
          if (res && res.code === '0') {
            this.$message.success('已提交导出，正在前往「导出记录」')
            // 带上 code 跳过去，列表就只剩这一类任务。
            // 那边没有为这个 code 注册筛选组件是**故意的**：筛选在本页已经做过了，
            // 注册组件就得改共享的导出页，没必要为此引入回归风险
            this.$router.push('/export/list?code=EEForCourseExamPaper')
          }
        })
    },
    openDetail(row) {
      this.detail = { visible: true, loading: true, data: null }
      const fetch = this.lecturerMode ? lecturerExamApi.detail : courseExamApi.fetchPaperDetail
      fetch(row.paperId).then(({ data }) => {
        this.detail.data = data
      }).finally(() => { this.detail.loading = false })
    },
    openVoid(row) {
      if (this.lecturerMode) return
      this.voidForm = { visible: true, saving: false, reason: '', paperId: row.paperId }
    },
    submitVoid() {
      if (this.lecturerMode) return
      if (!this.voidForm.reason || !this.voidForm.reason.trim()) {
        this.$message.warning('请填写作废原因')
        return
      }
      this.voidForm.saving = true
      courseExamApi.voidPaper({ paperId: this.voidForm.paperId, reason: this.voidForm.reason.trim() })
        .then(res => {
          if (res && res.code === '0') {
            this.voidForm.visible = false
            this.$message.success('已作废，考试次数已返还')
            this.doQueryList({})
          }
        }).finally(() => { this.voidForm.saving = false })
    },
    /** 把选项id串翻成 A/B/C：接口给的是 id，直接显示 id 运营看不懂 */
    codesOf(topic, ids) {
      if (!ids) return ''
      const map = {}
      ;(topic.options || []).forEach(o => { map[String(o.id)] = o.code })
      return String(ids).split(',').map(i => i.trim()).filter(Boolean).map(i => map[i] || i).join('、')
    },
    optionClass(option, topic) {
      const right = String(topic.correctAnswerIds || '').split(',').includes(String(option.id))
      const mine = String(topic.myAnswerIds || '').split(',').includes(String(option.id))
      return { 'opt-right': right, 'opt-mine': mine && !right }
    }
  }
}
</script>

<style scoped>
.sub {
  color: #909399;
  font-size: 12px;
}
.void-row {
  color: #F56C6C;
}
.detail-head {
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #f4f4f5;
  border-radius: 4px;
  font-size: 13px;
}
.topic {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #EBEEF5;
}
.topic-title {
  font-weight: bold;
  margin-bottom: 6px;
}
.option {
  padding: 2px 0 2px 14px;
  font-size: 13px;
}
.option-image {
  display: block;
  max-width: 100%;
  max-height: 280px;
  margin-top: 6px;
}
.opt-right {
  color: #67C23A;
  font-weight: bold;
}
.opt-mine {
  color: #F56C6C;
  text-decoration: line-through;
}
.resolution {
  margin-top: 4px;
  padding-left: 14px;
  color: #606266;
  font-size: 12px;
}
.void-tip {
  margin-bottom: 10px;
  color: #E6A23C;
  font-size: 13px;
  line-height: 1.6;
}
</style>
